import React, { useState } from 'react';
import { Box, Typography, Paper, Slide, Chip, Button } from '@mui/material';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import LaptopChromebookIcon from '@mui/icons-material/LaptopChromebook';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import PrintIcon from '@mui/icons-material/Print';
import { QueuedNewOrder } from '../hooks/useNewOrderQueue';

// ============================================================================
// INCOMING NEW ORDER NOTIFICATION — Sağ üst köşe pending sipariş bildirimi (O5)
// ============================================================================
//
// Sipariş bildirim kuyruğunda BEKLEYEN siparişler için sağ üstte gösterilen
// kalıcı bildirim. `IncomingCallNotification` ile aynı görsel dilde:
//   - Sağ üst köşe stack (stackIndex prop ile alt alta dizilir)
//   - Sarı/turuncu palet — pending (bekleyen) durumu
//   - Slide direction="left" animasyon
//   - Aktif sipariş için bildirim göstermiyoruz (zaten NewOrderModal full-screen açılıyor)
//
// "ONAYLA VE YAZDIR" BUTONU:
//   Kasiyer uzun bir telefon siparişi girerken (QuickSale modalı açık) biriken
//   platform siparişlerini BEKLETMEDEN buton ile onaylayıp yazdırabilir.
//   Buton `onAcceptAndPrint` prop'unu çağırır; sipariş pending kuyruğundan
//   anında çıkarıldığı için bu sipariş için NewOrderModal hiç açılmaz ve
//   sıradaki bildirim yukarı kayar. Buton dışındaki alan tıklamaya kapalıdır.
//
// Stack koordinasyonu (PackageOrderModals'da hesaplanır):
//   stackIndex = telephoneStackCount + pendingNewOrderIndex
//   Yemek bildirimleri telefon bildirimlerinin ALTINDA başlar.
// ============================================================================

// Telefon notification ile AYNI yükseklik sabitleri (telefon offset ile koordineli stack)
const NOTIFICATION_APPROX_HEIGHT = 220;
const STACK_GAP = 8;
const STACK_TOP_OFFSET = 16;

// Platform → görsel metadata
type PlatformVisualMeta = {
  label: string;
  color: string;
  icon: React.ReactNode;
};

const platformVisuals: Record<QueuedNewOrder['platform'], PlatformVisualMeta> = {
  getir: {
    label: 'GETİR',
    color: '#5D3EBC',
    icon: <RestaurantMenuIcon sx={{ fontSize: 20 }} />,
  },
  yemeksepeti: {
    label: 'YEMEKSEPETİ',
    color: '#FA0050',
    icon: <RestaurantMenuIcon sx={{ fontSize: 20 }} />,
  },
  migros: {
    label: 'MİGROS YEMEK',
    color: '#FF6600',
    icon: <RestaurantMenuIcon sx={{ fontSize: 20 }} />,
  },
  trendyol: {
    label: 'TRENDYOL YEMEK',
    color: '#F27A1A',
    icon: <RestaurantMenuIcon sx={{ fontSize: 20 }} />,
  },
  qr: {
    label: 'QR MENÜ',
    color: '#1976D2',
    icon: <QrCode2Icon sx={{ fontSize: 20 }} />,
  },
  kiosk: {
    label: 'KIOSK',
    color: '#388E3C',
    icon: <LaptopChromebookIcon sx={{ fontSize: 20 }} />,
  },
  ecommerce: {
    label: 'E-TİCARET',
    color: '#7B1FA2',
    icon: <ShoppingCartIcon sx={{ fontSize: 20 }} />,
  },
};

const formatTL = (n: number | null | undefined) =>
  typeof n === 'number' && !isNaN(n)
    ? `${n.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₺`
    : '—';

const deriveDisplayInfo = (q: QueuedNewOrder): {
  customerName: string;
  orderShortId: string;
  totalAmount: number | null;
} => {
  if (q.trendyolOrder) {
    const c = q.trendyolOrder.customer;
    const fullName =
      `${c?.firstName || ''} ${c?.lastName || ''}`.trim() ||
      `${q.trendyolOrder.address?.firstName || ''} ${q.trendyolOrder.address?.lastName || ''}`.trim();
    return {
      customerName: fullName || 'Bilinmeyen Müşteri',
      orderShortId: q.trendyolOrder.orderNumber || q.trendyolOrder.orderCode || '',
      totalAmount: q.trendyolOrder.totalPrice ?? null,
    };
  }
  const ms = q.masaSiparisi;
  return {
    customerName: ms?.musteriAdi || ms?.musteriTelefon || ms?.kullaniciAdi || 'Bilinmeyen Müşteri',
    orderShortId: ms?.siparisId || (ms?.id != null ? String(ms.id) : ''),
    totalAmount: ms?.toplamTutar ?? null,
  };
};

export interface IncomingNewOrderNotificationProps {
  open: boolean;
  queuedOrder: QueuedNewOrder;
  /** Stack içindeki konum (0 = en üstte). Telefon bildirimleri varsa caller offset ekler. */
  stackIndex: number;
  /** Bekleyen sipariş sırası (1, 2, ...) — başlıkta gösterilir. */
  pendingNumber: number;
  /**
   * "Onayla ve Yazdır" — sipariş modala düşmeden pending'den onaylanıp
   * yazdırılır. Verilmezse buton görünmez (eski salt-bilgi davranışı).
   */
  onAcceptAndPrint?: () => void;
}

const IncomingNewOrderNotification: React.FC<IncomingNewOrderNotificationProps> = ({
  open,
  queuedOrder,
  stackIndex,
  pendingNumber,
  onAcceptAndPrint,
}) => {
  const visual = platformVisuals[queuedOrder.platform];
  const info = deriveDisplayInfo(queuedOrder);

  // Tıklama sonrası anlık kilit — bildirim, sipariş kuyruktan çıkınca zaten
  // unmount olur; bu state yalnızca aynı render içinde çift tıklamayı ve
  // "basıldı mı?" belirsizliğini önler (asıl guard ekranda key bazlı).
  const [accepted, setAccepted] = useState(false);
  const handleAcceptClick = () => {
    if (accepted) return;
    setAccepted(true);
    onAcceptAndPrint?.();
  };

  const topOffset = STACK_TOP_OFFSET + stackIndex * (NOTIFICATION_APPROX_HEIGHT + STACK_GAP);
  const animationName = `newOrderPendingPulse_${stackIndex}`;

  return (
    <Slide direction="left" in={open} mountOnEnter unmountOnExit>
      <Paper
        elevation={12}
        // Buton dışındaki alan tıklamaya kapalı: Paper pointerEvents none,
        // yalnızca buton pointerEvents auto ile açılır (yanlışlık önleme).
        sx={{
          position: 'fixed',
          top: topOffset,
          right: 16,
          width: 360,
          maxWidth: 'calc(100vw - 32px)',
          // Telefon bildiriminden DAHA AŞAĞIDA zIndex (telefon notification = 1400 - i)
          // Yemek için 1380 base, stackIndex ile azalır
          zIndex: 1380 - stackIndex,
          borderRadius: 3,
          overflow: 'hidden',
          border: '2px solid #FF9800',
          boxShadow: '0 8px 32px rgba(255, 152, 0, 0.35)',
          pointerEvents: 'none',
        }}
      >
        {/* Pulse'lı başlık şeridi — sarı/turuncu (bekleyen sipariş) */}
        <Box
          sx={{
            bgcolor: '#FF9800',
            color: 'white',
            px: 2,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            animation: `${animationName} 2.5s infinite`,
            [`@keyframes ${animationName}`]: {
              '0%': { opacity: 1 },
              '50%': { opacity: 0.85 },
              '100%': { opacity: 1 },
            },
          }}
        >
          {visual.icon}
          <Typography variant="subtitle2" fontWeight="bold" sx={{ flex: 1 }}>
            BEKLEYEN SİPARİŞ #{pendingNumber}
          </Typography>
        </Box>

        {/* İçerik: platform + sipariş bilgisi */}
        <Box sx={{ p: 2, textAlign: 'center' }}>
          <Chip
            label={visual.label}
            size="small"
            sx={{
              bgcolor: visual.color,
              color: 'white',
              fontWeight: 'bold',
              fontSize: '0.75rem',
              mb: 1,
            }}
          />
          <Typography
            variant="subtitle1"
            fontWeight="bold"
            sx={{ wordBreak: 'break-word' }}
          >
            {info.customerName}
          </Typography>
          {info.orderShortId && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              #{info.orderShortId}
            </Typography>
          )}
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{ color: visual.color, mt: 0.5 }}
          >
            {formatTL(info.totalAmount)}
          </Typography>

          {onAcceptAndPrint && (
            <Button
              variant="contained"
              color="success"
              size="medium"
              fullWidth
              startIcon={<PrintIcon />}
              onClick={handleAcceptClick}
              disabled={accepted}
              sx={{ mt: 1.5, fontWeight: 'bold', pointerEvents: 'auto' }}
            >
              {accepted ? 'İşleniyor...' : 'Onayla ve Yazdır'}
            </Button>
          )}

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}
          >
            {onAcceptAndPrint
              ? 'Ekran müsait olunca otomatik açılacak — veya bekletmeden onaylayın'
              : 'Ekran müsait olunca otomatik açılacak'}
          </Typography>
        </Box>
      </Paper>
    </Slide>
  );
};

export default IncomingNewOrderNotification;
